// Derived from: https://github.com/symfony/translation/blob/460390765eb7bb9338a4a323b8a4e815a47541ba/Interval.php
const intervalRegexp =
  /^({\s*(-?\d+(\.\d+)?[\s*,\s*\-?\d+(.\d+)?]*)\s*})|([[\]])\s*(-Inf|\*|-?\d+(\.\d+)?)\s*,\s*(\+?Inf|\*|-?\d+(\.\d+)?)\s*([[\]])$/;
const anyIntervalRegexp =
  /({\s*(-?\d+(\.\d+)?[\s*,\s*\-?\d+(.\d+)?]*)\s*})|([[\]])\s*(-Inf|\*|-?\d+(\.\d+)?)\s*,\s*(\+?Inf|\*|-?\d+(\.\d+)?)\s*([[\]])/;

type FormattedMessageWithRules = {
  messages: string[];
  foundMessage: string | null;
};

export const formatMessagesWithAmount = (
  translation: string,
  amount: number
): FormattedMessageWithRules => {
  // Separate the plural from the singular, if any
  const messages = translation.split("|");

  const rules: string[] = [];

  for (let iteration = 0; iteration < messages.length; iteration++) {
    messages[iteration] = messages[iteration].trim();

    if (anyIntervalRegexp.test(messages[iteration])) {
      // Split rule and message
      const ruleAndMessage = messages[iteration].split(/\s/);
      // I.e. `{0}`, `{1}`, `[2,19]`, `[20,*]`
      const rule: string = ruleAndMessage.shift() || "";
      rules.push(rule);
      messages[iteration] = ruleAndMessage.join(" ");
    }
  }

  // Check the explicit rules
  for (let iteration = 0; iteration < rules.length; iteration++) {
    if (assertExplicitNumberRule(amount, rules[iteration])) {
      return {
        messages,
        foundMessage: messages[iteration],
      };
    }
  }

  return {
    messages,
    foundMessage: null,
  };
};

const assertExplicitNumberRule = (count: number, interval: string): boolean => {
  /**
   * From the Symfony\Component\Translation\Interval Docs
   *
   * Tests if a given number belongs to a given math interval.
   *
   * An interval can represent a finite set of numbers:
   *
   *  {1,2,3,4}
   *
   * An interval can represent numbers between two numbers:
   *
   *  [1, +Inf]
   *  ]-1,2[
   *
   * The left delimiter can be [ (inclusive) or ] (exclusive).
   * The right delimiter can be [ (exclusive) or ] (inclusive).
   * Besides numbers, you can use -Inf and +Inf for the infinite.
   */

  let matches: RegExpMatchArray | null = interval.trim().match(intervalRegexp);
  if (!matches) {
    return false;
  }

  if (matches[2]) {
    const items = matches[2].split(",");
    for (const itemIteration of items) {
      if (Number.parseInt(itemIteration, 10) === count) {
        return true;
      }
    }

    return false;
  }

  // Remove falsy values.
  matches = matches.filter((match: string) => !!match);

  const leftDelimiter = matches[1];
  let leftNumber = convertToNumber(matches[2]);
  if (leftNumber === Number.POSITIVE_INFINITY) {
    leftNumber = Number.NEGATIVE_INFINITY;
  }

  const rightNumber = convertToNumber(matches[3]);
  const rightDelimiter = matches[4];

  return (
    (leftDelimiter === "[" ? count >= leftNumber : count > leftNumber) &&
    (rightDelimiter === "]" ? count <= rightNumber : count < rightNumber)
  );
};

const convertToNumber = (value: string): number => {
  if (value === "-Inf") {
    return Number.NEGATIVE_INFINITY;
  }

  if (value === "+Inf" || value === "Inf" || value === "*") {
    return Number.POSITIVE_INFINITY;
  }

  return Number.parseInt(value, 10);
};
